import {
  consumePendingFileFromIdb,
  uploadFile,
  usePendingFileStore,
} from "@bandwith/recordings";
import { ChordProHelp, ChordProRenderer } from "@bandwith/songs";
import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, Clock, Music } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ChordProEditor } from "../components/ChordProEditor";
import { FormActionButtons } from "../components/FormActionButtons";
import { RecordingActions } from "../components/RecordingActions";
import { api } from "../lib/api";
import { useSession } from "../lib/auth";
import { useSignInRedirect } from "../lib/useSignInRedirect";
import { songNewRoute } from "../router";

export function SongNew() {
  const navigate = useNavigate();
  const { action: initialAction } = songNewRoute.useSearch();
  const { session, isLoading: sessionLoading } = useSession();
  const signInRedirect = useSignInRedirect();
  const isSignedIn = !!session?.user;
  const currentUserId = session?.user?.id ?? null;
  const consumePendingFile = usePendingFileStore((s) => s.consumePendingFile);

  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [debouncedNotes, setDebouncedNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedNotes(notes);
    }, 150);
    return () => clearTimeout(timeout);
  }, [notes]);

  useEffect(() => {
    const zustandFile = consumePendingFile();
    if (zustandFile) {
      setPendingFiles([zustandFile]);
      return;
    }
    if (initialAction === "upload") {
      consumePendingFileFromIdb().then((file) => {
        if (file) setPendingFiles([file]);
      });
    }
  }, [consumePendingFile, initialAction]);

  async function getUploadUrl(
    fileName: string,
    fileType: string,
    fileSize: number,
  ) {
    const response = await api.upload.url.$get({
      query: { fileName, fileType, fileSize: String(fileSize) },
    });
    if (!response.ok) throw new Error("Failed to get upload URL");
    return response.json();
  }

  const handleFileAdd = (file: File) => {
    setPendingFiles((prev) => [...prev, file]);
  };

  const handleFileRemove = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!currentUserId) {
        throw new Error("Must be signed in to create a song");
      }
      const response = await api.songs.$post({
        json: {
          title: title || "Untitled Song",
          notes: notes || undefined,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to create song");
      }
      const { songId } = await response.json();

      if (pendingFiles.length > 0) {
        const uploadResults = await Promise.all(
          pendingFiles.map((file) => uploadFile(file, getUploadUrl)),
        );
        for (const { storageKey, durationSeconds } of uploadResults) {
          const recordingResponse = await api.songs[":id"].recordings.$post({
            param: { id: songId },
            json: { storageKey, durationSeconds },
          });
          if (!recordingResponse.ok) {
            console.error("Failed to add recording");
          }
        }
      }

      return { songId };
    },
    onSuccess: (data) => {
      navigate({ to: "/songs/$id", params: { id: data.songId } });
    },
    onError: (err) => {
      console.error("Create song error:", err);
      setError(err instanceof Error ? err.message : "Failed to create song");
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isSignedIn) {
      signInRedirect("/songs/new");
      return;
    }

    createMutation.mutate();
  };

  const handleCancel = () => {
    navigate({ to: "/" });
  };

  const isPending = createMutation.isPending;

  if (sessionLoading) {
    return <div className="text-text-muted">Loading...</div>;
  }

  // Handler for Cmd/Ctrl+Enter from ChordProEditor
  const handleEditorSubmit = () => {
    formRef.current?.requestSubmit();
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit}>
      {/* Navigation */}
      <nav className="mb-8 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="group inline-flex items-center gap-2 text-sm text-text-muted transition-colors hover:text-amber"
          >
            <span className="transition-transform duration-150 group-hover:-translate-x-1">
              <ChevronLeft className="h-4 w-4" />
            </span>
            All Songs
          </Link>
          <FormActionButtons
            mode="create"
            isPending={isPending}
            error={error}
            onCancel={handleCancel}
          />
        </div>
      </nav>

      {/* Header with title input */}
      <header className="mb-10">
        <input
          ref={titleInputRef}
          name="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={isPending}
          placeholder="Untitled Song"
          className="w-full rounded-lg border border-amber bg-bg-secondary px-4 py-2 font-display text-3xl text-text-primary placeholder-text-muted transition-colors focus:outline-none disabled:opacity-50"
        />
      </header>

      {/* Recordings section */}
      <section>
        <h2 className="mb-4 font-mono text-xs font-medium uppercase tracking-widest text-text-muted">
          Recordings
        </h2>
        {pendingFiles.length > 0 ? (
          <ul className="space-y-3">
            {pendingFiles.map((file, index) => (
              <li
                key={`${file.name}-${file.lastModified}`}
                className="rounded-lg border border-dashed border-border-subtle bg-bg-secondary/50 p-4 opacity-60"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-text-muted">
                    <Clock aria-hidden="true" className="h-4 w-4" />
                  </div>
                  <span className="flex-1 truncate font-mono text-sm text-text-muted">
                    {file.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleFileRemove(index)}
                    disabled={isPending}
                    className="shrink-0 text-sm text-text-muted hover:text-red-muted disabled:opacity-50"
                    aria-label={`Remove ${file.name}`}
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border-subtle bg-bg-secondary/50 py-12 text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-bg-tertiary">
              <Music className="h-6 w-6 text-text-muted" aria-hidden="true" />
            </div>
            <p className="font-display text-lg text-text-secondary">
              No recordings yet
            </p>
            <p className="mt-1 text-sm text-text-muted">
              Add a recording to your new song
            </p>
          </div>
        )}
        <RecordingActions
          onRecordingComplete={handleFileAdd}
          autoStart={initialAction === "record"}
        />
      </section>

      {/* Notes section with live preview */}
      <section className="mt-10">
        <h2 className="mb-4 font-mono text-xs font-medium uppercase tracking-widest text-text-muted">
          Notes
        </h2>
        <div className="flex flex-col gap-4 lg:w-[min(200%,100vw)] lg:flex-row">
          {/* Editor pane */}
          <div className="relative min-w-0 flex-1 lg:w-1/2 lg:flex-none">
            <span className="mb-2 flex items-center font-mono text-xs uppercase tracking-widest text-text-muted">
              ChordPro
              <ChordProHelp />
            </span>
            <ChordProEditor
              value={notes}
              onChange={setNotes}
              onSubmit={handleEditorSubmit}
              disabled={isPending}
            />
            {/* Hidden input to include notes in form submission */}
            <input type="hidden" name="notes" value={notes} />
          </div>
          {/* Live preview pane - uses debounced notes to reduce re-renders */}
          <div className="min-w-0 flex-1">
            <span className="mb-2 block font-mono text-xs uppercase tracking-widest text-text-muted">
              Preview
            </span>
            <div className="rounded-lg border border-border-subtle bg-bg-secondary p-5">
              <ChordProRenderer content={debouncedNotes} />
            </div>
          </div>
        </div>
      </section>
    </form>
  );
}
